import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Users, Calendar, TrendingUp, Award, UserMinus, Flame, BarChart3, Trophy } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-sm px-3 py-2 text-xs">
      <p className="text-[#FAFAFA] font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratingDetails, setRatingDetails] = useState({});
  const [showingRatings, setShowingRatings] = useState(null);
  const [allPRs, setAllPRs] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, prsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/pr-logs/all-prs`).catch(() => ({ data: [] }))
      ]);
      setData(analyticsRes.data);
      setAllPRs(prsRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatingVoters = async (wodId) => {
    if (showingRatings === wodId) { setShowingRatings(null); return; }
    try {
      const response = await axios.get(`${API}/wods/${wodId}/ratings-detail`);
      setRatingDetails({ ...ratingDetails, [wodId]: response.data });
      setShowingRatings(wodId);
    } catch (error) {
      // silently fail
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading analytics...</p>
      </div>
    );
  }

  if (!data) return null;

  const { key_metrics, attendance_trend, class_popularity, peak_hours, wod_difficulty, top_attendees, inactive_members } = data;

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="key-metrics">
        <Card className="bg-[#18181B] border-[#27272A] p-5">
          <div className="flex items-center gap-3">
            <div className="bg-[#CCFF00]/10 p-2.5 rounded-sm">
              <Users className="w-5 h-5 text-[#CCFF00]" />
            </div>
            <div>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Athletes</p>
              <p className="text-2xl font-heading font-black text-[#FAFAFA]">{key_metrics.total_members}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-[#18181B] border-[#27272A] p-5">
          <div className="flex items-center gap-3">
            <div className="bg-[#22C55E]/10 p-2.5 rounded-sm">
              <TrendingUp className="w-5 h-5 text-[#22C55E]" />
            </div>
            <div>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Attendance Rate</p>
              <p className="text-2xl font-heading font-black text-[#FAFAFA]">{key_metrics.avg_attendance_rate}%</p>
            </div>
          </div>
        </Card>

        <Card className="bg-[#18181B] border-[#27272A] p-5">
          <div className="flex items-center gap-3">
            <div className="bg-[#2563EB]/10 p-2.5 rounded-sm">
              <Calendar className="w-5 h-5 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Classes This Week</p>
              <p className="text-2xl font-heading font-black text-[#FAFAFA]">{key_metrics.classes_this_week}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-[#18181B] border-[#27272A] p-5">
          <div className="flex items-center gap-3">
            <div className="bg-[#F59E0B]/10 p-2.5 rounded-sm">
              <Award className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <div>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">New This Month</p>
              <p className="text-2xl font-heading font-black text-[#FAFAFA]">{key_metrics.new_members_this_month}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Most Popular Class Banner */}
      <Card className="bg-gradient-to-r from-[#CCFF00]/5 to-transparent border-[#CCFF00]/20 p-5">
        <div className="flex items-center gap-3">
          <Flame className="w-6 h-6 text-[#CCFF00]" />
          <div>
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Most Popular Class</p>
            <p className="text-xl font-heading font-bold uppercase text-[#CCFF00]">{key_metrics.most_popular_class}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-xs text-[#A1A1AA]">Total</p>
            <p className="text-lg font-bold text-[#FAFAFA]">{key_metrics.total_bookings} bookings / {key_metrics.total_checkins} check-ins</p>
          </div>
        </div>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Trend */}
        <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="attendance-trend-chart">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#CCFF00]" />
            <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">Weekly Attendance</h3>
          </div>
          <p className="text-xs text-[#A1A1AA] mb-4">Last 8 weeks — bookings vs attended</p>
          {attendance_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={attendance_trend} barGap={2}>
                <XAxis dataKey="week" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(204,255,0,0.05)' }} />
                <Bar dataKey="bookings" name="Bookings" fill="#27272A" radius={[3, 3, 0, 0]} />
                <Bar dataKey="attended" name="Attended" fill="#CCFF00" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[#52525B] text-sm text-center py-10">No attendance data yet</p>
          )}
        </Card>

        {/* Peak Hours */}
        <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="peak-hours-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#CCFF00]" />
            <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">Peak Hours</h3>
          </div>
          <p className="text-xs text-[#A1A1AA] mb-4">Bookings by time slot</p>
          {peak_hours.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={peak_hours}>
                <XAxis dataKey="hour" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(204,255,0,0.05)' }} />
                <Bar dataKey="bookings" name="Bookings" radius={[3, 3, 0, 0]}>
                  {peak_hours.map((_, i) => (
                    <Cell key={i} fill={i === peak_hours.reduce((maxI, h, idx, arr) => h.bookings > arr[maxI].bookings ? idx : maxI, 0) ? '#CCFF00' : '#3F3F46'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[#52525B] text-sm text-center py-10">No class data yet</p>
          )}
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Attendees */}
        <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="top-attendees">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-[#CCFF00]" />
            <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">Top Attendees</h3>
          </div>
          {top_attendees.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {top_attendees.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-[#09090B] rounded-sm border border-[#27272A]">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      i === 0 ? 'bg-[#CCFF00] text-black' : i === 1 ? 'bg-[#A1A1AA] text-black' : i === 2 ? 'bg-[#92400E] text-white' : 'bg-[#27272A] text-[#A1A1AA]'
                    }`}>{i + 1}</span>
                    <span className="text-sm text-[#FAFAFA] font-semibold truncate">{a.name}</span>
                  </div>
                  <span className="text-sm text-[#CCFF00] font-bold">{a.checkin_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#52525B] text-sm text-center py-6">No check-in data yet</p>
          )}
        </Card>

        {/* WOD Difficulty & Voters */}
        <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="wod-difficulty">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#CCFF00]" />
            <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">WOD Ratings</h3>
          </div>
          <p className="text-xs text-[#52525B] mb-3">Click a WOD to see who voted</p>
          {wod_difficulty.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {wod_difficulty.map((w, i) => (
                <div key={i}>
                  <div
                    className="p-2.5 bg-[#09090B] rounded-sm border border-[#27272A] cursor-pointer hover:border-[#3F3F46] transition-colors"
                    onClick={() => w.wod_id && fetchRatingVoters(w.wod_id)}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-[#FAFAFA] font-semibold truncate block">{w.title}</span>
                        {w.date && <span className="text-xs text-[#52525B]">{new Date(w.date).toLocaleDateString()}</span>}
                      </div>
                      <span className={`text-sm font-bold ${
                        w.avg_rating <= 3 ? 'text-[#22C55E]' : w.avg_rating <= 6 ? 'text-[#CCFF00]' : w.avg_rating <= 8 ? 'text-[#F97316]' : 'text-[#EF4444]'
                      }`}>{w.avg_rating}/10</span>
                    </div>
                    <div className="w-full bg-[#27272A] rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: `${(w.avg_rating / 10) * 100}%`,
                          backgroundColor: w.avg_rating <= 3 ? '#22C55E' : w.avg_rating <= 6 ? '#CCFF00' : w.avg_rating <= 8 ? '#F97316' : '#EF4444'
                        }}
                      />
                    </div>
                    <p className="text-xs text-[#52525B] mt-1">{w.count} {w.count === 1 ? 'vote' : 'votes'}</p>
                  </div>
                  {showingRatings === w.wod_id && ratingDetails[w.wod_id] && (
                    <div className="ml-3 mt-1 space-y-1 border-l-2 border-[#27272A] pl-3">
                      {ratingDetails[w.wod_id].map((r, j) => (
                        <div key={j} className="flex items-center justify-between text-xs py-1">
                          <span className="text-[#A1A1AA]">{r.user_name}</span>
                          <span className="text-[#FAFAFA] font-semibold">{r.rating}/10</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#52525B] text-sm text-center py-6">No WOD ratings yet</p>
          )}
        </Card>

        {/* Inactive Members */}
        <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="inactive-members">
          <div className="flex items-center gap-2 mb-4">
            <UserMinus className="w-5 h-5 text-[#EF4444]" />
            <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">Inactive (2+ weeks)</h3>
          </div>
          {inactive_members.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {inactive_members.map((m, i) => (
                <div key={i} className="p-2.5 bg-[#09090B] rounded-sm border border-[#27272A]">
                  <p className="text-sm text-[#FAFAFA] font-semibold">{m.name}</p>
                  <p className="text-xs text-[#52525B]">
                    Last seen: {m.last_attendance === 'Never' ? 'Never attended' : new Date(m.last_attendance).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#22C55E] text-sm text-center py-6">All members are active!</p>
          )}
        </Card>
      </div>

      {/* PR Leaderboard */}
      {allPRs.length > 0 && (
        <div data-testid="pr-leaderboard">
          <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA] mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#CCFF00]" />
            PR Leaderboard
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allPRs.map((item, i) => (
              <Card key={i} className="bg-[#18181B] border-[#27272A] p-4">
                <h4 className="text-base font-heading font-bold uppercase text-[#CCFF00] mb-3">{item.movement.name}</h4>
                <div className="space-y-1.5">
                  {item.top_prs.map((pr, j) => (
                    <div key={j} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                          j === 0 ? 'bg-[#CCFF00] text-black' : j === 1 ? 'bg-[#A1A1AA] text-black' : j === 2 ? 'bg-[#92400E] text-white' : 'bg-[#27272A] text-[#A1A1AA]'
                        }`}>{j + 1}</span>
                        <span className="text-[#FAFAFA]">{pr.user_name}</span>
                      </div>
                      <span className="text-[#CCFF00] font-bold">{pr.best_value} {item.movement.unit}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
